// Colour, format and tooltip-key helpers for rendering an Objectives row's figures — current-value
// colour, mortality-bar fill colour, Pigs to Target colour/format/tooltip, and trend-arrow colour.
// Row translation and business rules live in objectiveRow.ts instead; this module only turns an
// already-resolved row/value into a display decision.
//
// Pure, no Vue/store dependencies, so every export here is unit-testable in isolation.

import { isInDeviation, type DeviationValues } from './deviation';
import type { TrendDirection } from './objectiveRow';

// Unified colour scheme for a Barn/Section's current-value cell: red once in Deviation, green
// otherwise, neutral when the comparison itself is unknown (missing value or target) — that must not
// silently read as "all good". Delegates the actual comparison to deviation.ts's isInDeviation so the
// row-highlighting and the value colour never disagree about what counts as a deviation.
export const OBJECTIVE_COLOUR_RED = 'text-red-600';
export const OBJECTIVE_COLOUR_GREEN = 'text-green-700';
export const OBJECTIVE_COLOUR_NEUTRAL = 'text-gray-400';

export function objectiveValueColourClass(row: DeviationValues): string {
    if (row.currentValue == null || row.resolvedTarget == null) return OBJECTIVE_COLOUR_NEUTRAL;
    return isInDeviation(row) ? OBJECTIVE_COLOUR_RED : OBJECTIVE_COLOUR_GREEN;
}

// Same red/green verdict, as a bar-fill background instead of text colour — the mortality bar cell
// (BarnRow.vue/SectionRow.vue) is only ever rendered once currentValue and resolvedTarget are both
// known (see the `else` "plain text, no bar" branch there), so this doesn't need the neutral case.
export function objectiveBarFillClass(row: DeviationValues): string {
    return isInDeviation(row) ? 'bg-red-500' : 'bg-green-500';
}

// Pigs to Target badge colour — sign-based rather than routed back through
// isInDeviation/DeviationValues: the backend already computes this figure so its sign agrees with
// IsDeviation by construction (see MortalityObjectiveEvaluator.ComputePigsToTarget), and re-deriving
// the verdict here from currentValue/resolvedTarget a second time would just be duplicate logic that
// could drift. Zero (exactly at target) reads as green, matching isWithinTarget's own "not a
// deviation" call at that boundary.
//
// Sign convention: positive = breached (that many pigs over), negative = headroom (that many pigs
// could still die) - the API's PigsToTargetCalculator negates the raw value at the source, so this
// reads the OPPOSITE way from the figure's own name ("Pigs to Target") might suggest. Colour meaning
// itself hasn't changed (green is still "doing well", red still "breached") - only which sign each
// one is printed under.
export function pigsToTargetColourClass(pigsToTarget: number | null): string {
    if (pigsToTarget == null) return OBJECTIVE_COLOUR_NEUTRAL;
    return pigsToTarget > 0 ? OBJECTIVE_COLOUR_RED : OBJECTIVE_COLOUR_GREEN;
}

// Explicit "+" for the positive case (JS's own `-12` stringifies with its sign already) - shown
// parenthesised next to the % in BarnRow.vue/SectionRow.vue: "+45" reads as headroom, "-12" as
// overshoot, with no sign at all for the exact-zero boundary.
export function formatPigsToTarget(pigsToTarget: number): string {
    return pigsToTarget > 0 ? `+${pigsToTarget}` : `${pigsToTarget}`;
}

// Which i18n key explains a given Pigs to Target figure - the two tooltip keys' own text carries
// the actual explanation, this only picks which one applies; each is plural-aware ($tc) since the
// count is almost never exactly 1. Null at exactly zero (no margin either way) - every phrasing
// tried there ("no more pigs can die", "0 pigs to target") read as either alarming or vacuous for
// what is, numerically, the same "right on target" case the badge's own colour already conveys.
//
// Same sign convention as pigsToTargetColourClass above (negative = headroom/margin, positive =
// already over target) - kept in sync with it deliberately: a mismatched flip here would print the
// "X more have died than allowed" text under a green/headroom badge, or vice versa.
export type PigsToTargetTooltipKey = 'pigsToTargetMarginTooltip' | 'pigsToTargetOverTooltip';

export function pigsToTargetTooltipKey(pigsToTarget: number): PigsToTargetTooltipKey | null {
    if (pigsToTarget < 0) return 'pigsToTargetMarginTooltip';
    if (pigsToTarget > 0) return 'pigsToTargetOverTooltip';
    return null;
}

// Same red/green pair as objectiveValueColourClass above, not a fresh `text-green-600`/`text-red-600`
// pick - this theme's Tailwind config overrides green-600 to a pale `#CDDDDD` (a chart-gridline
// colour, not a "success" one), so picking shades ad hoc silently stops looking green. green-700/
// red-600 are the pair every other verdict colour in this feature already uses.
export function trendDirectionColourClass(direction: TrendDirection): string {
    return direction === 'improved' ? OBJECTIVE_COLOUR_GREEN : OBJECTIVE_COLOUR_RED;
}
